/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { Subscription } from 'rxjs';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { DashboardFacade } from 'src/app/modules/core/store/dashboard/dashboard.facade';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import {
  ILoopDetail,
  ITotalExtraction,
  ITotalExtractionLoops,
  ITotalExtractionTable,
} from '../../../../widget/interfaces/total-extraction';
import { TotalExtractionService } from '../../../services/total-extraction/total-extraction.service';
import { DashboardWidgetBasicComponent } from '../../dashboard-widget-basic/dashboard-widget-basic.component';

@Component({
  selector: 'app-total-extraction',
  templateUrl: './total-extraction.component.html',
  styleUrls: ['./total-extraction.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TotalExtractionComponent extends DashboardWidgetBasicComponent implements OnInit, OnDestroy {
  public totalShelvesPercent: string;
  public scheduledShelvesPercent: string;
  public showTable: boolean;
  public rawComponentName: string;
  public hasDataTable: boolean;

  public progressBarLoops: ITotalExtractionLoops;
  public totalExtractionData: ITotalExtraction;
  public totalExtractionTable: ITotalExtractionTable[];
  public firstLoop: ILoopDetail[];
  public lastLoop: ILoopDetail[];
  public lastData: any;
  private totalLoopsSubscription: Subscription;
  private firstLoopSubscription: Subscription;
  private lastLoopSubscription: Subscription;

  public showHasTable = false;
  public componentName = '';
  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    public cacheService: CacheService,
    public machineContext: MachineContextService,
    public totalExtractionSvc: TotalExtractionService,
    public activeRoute: ActivatedRoute,
    public screenViewService: ScreenViewService,
    public dashboardFacade: DashboardFacade,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.initDefaultValuesAndParams();
  }

  /**
   * Init default values & request params
   */
  private initDefaultValuesAndParams(): void {
    this.progressBarLoops = {
      totalLoop: 0,
      scheduledLoop: 0,
      remnantLoop: 0,
    };

    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * onInit component
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.hasDataTable = false;
    this.totalExtractionTable = [];
    this.lastData = {};
    this.dashboardFacade.dashboardModel$.subscribe((value) => {
      this.showHasTable = value.isShowTable;
      this.componentName = value.rawComponent?.visibleName;
    });
  }

  /**
   * Function to build view
   */
  public buildView(values: any): void {
    super.buildView(values);
    this.getData(values);
  }

  /**
   * Function to get widget data
   */
  private getData(values: ITotalExtraction): void {
    this.totalExtractionData = values;

    const allZeros = this.totalExtractionData.totalLoop === 0 && this.totalExtractionData.scheduledLoop === 0;

    if (allZeros) {
      this.chartWithoutData$.next(true);
    } else this.chartWithoutData$.next(false);

    this.buildTableData(this.totalExtractionData);
    this.getProgressBarLoops(this.totalExtractionData);
    this.getFirstLoop(this.totalExtractionData);
    this.getLastLoop(this.totalExtractionData);
    this.verifyHasData();
  }
  /**
   * Function to build the data of table
   */
  private buildTableData(totalExtractionData: ITotalExtraction): void {
    this.totalExtractionTable = [];
    totalExtractionData.tbTotalExtraction.map((tb: ITotalExtractionTable) => {
      return this.totalExtractionTable.push({
        shiftName: tb.shiftName,
        firstLoop: tb.firstLoop,
        shiftPeriodId: tb.shiftPeriodId,
        startShift: tb.startShift,
        firstPart: tb.firstPart,
        relief: tb.relief,
        secondPart: tb.secondPart,
        endShift: tb.endShift,
        overlap: tb.overlap,
        totalLoops: tb.totalLoops,
        lastLoop: tb.lastLoop,
      });
    });
    if (this.totalExtractionData.tbTotalExtraction.length > 0) {
      if (JSON.stringify(this.lastData) !== JSON.stringify(this.totalExtractionData.tbTotalExtraction)) {
        this.showHasTable = false;
        this.lastData = this.totalExtractionData.tbTotalExtraction;
      } else {
        this.lastData = this.totalExtractionData.tbTotalExtraction;
      }
    }
    this.hasDataTable = this.totalExtractionData.tbTotalExtraction.length > 0;
  }
  /**
   * Function to get total, scheduled & remnant loops
   *
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private getProgressBarLoops(totalExtractionData: ITotalExtraction): void {
    this.progressBarLoops.totalLoop = totalExtractionData.totalLoop;
    this.progressBarLoops.scheduledLoop = totalExtractionData.scheduledLoop;
    this.calculatePercent();
  }

  /**
   * Function to get first loop from each shift
   *
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private getFirstLoop(totalExtractionData: ITotalExtraction): void {
    this.firstLoop = totalExtractionData.firstLoop.sort((a, b) => parseFloat(a.shiftOrder) - parseFloat(b.shiftOrder));
  }

  /**
   * Function to get last loop from each shift
   *
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private getLastLoop(totalExtractionData: ITotalExtraction): void {
    this.lastLoop = totalExtractionData.lastLoop.sort((a, b) => parseFloat(a.shiftOrder) - parseFloat(b.shiftOrder));
  }

  /**
   * Function to calculate percent of total, scheduled & remnant loops
   * Assign the percentage for each progress bar section
   */
  private calculatePercent(): void {
    const doneLoops = this.progressBarLoops.totalLoop;
    const scheduledLoops = this.progressBarLoops.scheduledLoop;
    const remnantLoops = this.progressBarLoops.remnantLoop;

    const total = doneLoops + scheduledLoops + remnantLoops;
    const pendingTotal = scheduledLoops + remnantLoops;
    const totalDoneOverScheduled = doneLoops - scheduledLoops;

    if (total > 0) {
      if (doneLoops < scheduledLoops) {
        this.totalShelvesPercent = `${((100 * doneLoops) / pendingTotal).toFixed(1)}%`;
        this.scheduledShelvesPercent = `${((100 * (scheduledLoops - doneLoops)) / pendingTotal).toFixed(1)}%`;
      } else if (doneLoops < pendingTotal) {
        this.totalShelvesPercent = `${((100 * doneLoops) / pendingTotal).toFixed(1)}%`;
        this.scheduledShelvesPercent = '0%';
      } else {
        this.totalShelvesPercent = '100%';
        this.scheduledShelvesPercent = '0%';
      }
    } else {
      this.totalShelvesPercent = '0%';
      this.scheduledShelvesPercent = '0%';
    }
  }

  /**
   * Function to verify if widget has data
   */
  private verifyHasData(): void {
    if ((this.firstLoop && this.lastLoop) || this.progressBarLoops.totalLoop > 0 || this.progressBarLoops.scheduledLoop > 0) {
      this.hasData = true;
    }
  }

  /**
   * onDestroy component, unsubcribe observables
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.totalLoopsSubscription) {
      this.totalLoopsSubscription.unsubscribe();
    }
    if (this.firstLoopSubscription) {
      this.firstLoopSubscription.unsubscribe();
    }
    if (this.lastLoopSubscription) {
      this.lastLoopSubscription.unsubscribe();
    }
  }
}
